import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaCheckCircle } from 'react-icons/fa';
import { db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const Container = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SuccessIcon = styled.div`
  color: #28a745;
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const OrderNumber = styled.div`
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  
  p {
    margin: 0;
    color: #666;
  }
  
  strong {
    color: #333;
    font-size: 1.2rem;
    display: block;
    margin-top: 0.5rem;
  }
`;

const OrderDetails = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem 0;
  text-align: left;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.span`
  flex: 2;
`;

const ItemQuantity = styled.span`
  flex: 1;
  text-align: center;
`;

const ItemPrice = styled.span`
  flex: 1;
  text-align: right;
`;

const OrderSummary = styled.div`
  margin-top: 1.5rem;
  
  div {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    
    &.total {
      font-weight: bold;
      border-top: 1px solid #ddd;
      margin-top: 0.5rem;
      padding-top: 1rem;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Button = styled(Link)`
  padding: 0.75rem 1.5rem;
  border: ${props => props.primary ? 'none' : '1px solid #C41E3A'};
  border-radius: 4px;
  background-color: ${props => props.primary ? '#C41E3A' : 'white'};
  color: ${props => props.primary ? 'white' : '#C41E3A'};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background-color: ${props => props.primary ? '#A01930' : '#f8f8f8'};
  }
`;

const OrderConfirmation = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Get order ID from localStorage
        const orderId = localStorage.getItem('pendingOrderId');
        
        if (!orderId) {
          // If no order ID in localStorage, check if we have checkout info
          const checkoutInfo = localStorage.getItem('checkoutInfo');
          if (checkoutInfo) {
            const parsedInfo = JSON.parse(checkoutInfo);
            setOrder({
              id: parsedInfo.orderId || 'MCF-' + Math.floor(100000 + Math.random() * 900000),
              customerInfo: parsedInfo.customerInfo,
              orderDetails: parsedInfo.orderDetails,
              status: 'completed',
            });
            setLoading(false);
            return;
          }
          
          throw new Error('No order information found');
        }
        
        // Get order from Firestore
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        
        if (!orderDoc.exists()) {
          throw new Error('Order not found');
        }
        
        const orderData = orderDoc.data();
        
        // Update order status to pending (payment confirmed, awaiting fulfillment)
        await updateDoc(doc(db, 'orders', orderId), {
          status: 'pending',
          paymentStatus: 'paid',
          paidAt: new Date(),
          updatedAt: new Date(),
        });
        
        // Send order confirmation email now that payment is confirmed
        try {
          await fetch('https://benevolent-sfogliatella-83141e.netlify.app/api/send-order-confirmation', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              customerEmail: orderData.customerInfo.email,
              customerName: orderData.customerInfo.name,
              orderId: orderId,
              orderItems: orderData.orderDetails.items,
              shippingAddress: {
                name: orderData.customerInfo.name,
                address: orderData.customerInfo.address,
                city: orderData.customerInfo.city,
                state: orderData.customerInfo.state,
                zipCode: orderData.customerInfo.zipCode,
                country: orderData.customerInfo.country
              },
              subtotal: orderData.orderDetails.subtotal,
              shipping: orderData.orderDetails.shipping,
              tax: orderData.orderDetails.tax,
              total: orderData.orderDetails.total
            }),
          });
          console.log('Order confirmation email sent after payment');
        } catch (emailError) {
          console.error('Failed to send order confirmation email:', emailError);
          // Don't fail the order if email fails
        }
        
        // Set order data
        setOrder({
          id: orderId,
          ...orderData,
          status: 'pending',
          paymentStatus: 'paid',
        });
        
        // Clear cart and pending order ID
        localStorage.removeItem('pendingOrderId');
        
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Could not retrieve order details. Please contact customer support.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, []);
  
  if (loading) {
    return (
      <Container>
        <Title>Loading order details...</Title>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container>
        <Title>Something went wrong</Title>
        <Message>{error}</Message>
        <ButtonGroup>
          <Button to="/" primary>Return to Home</Button>
        </ButtonGroup>
      </Container>
    );
  }
  
  return (
    <Container>
      <SuccessIcon>
        <FaCheckCircle />
      </SuccessIcon>
      <Title>Order Confirmed!</Title>
      <Message>
        Thank you for your purchase. We've received your order and are processing it now.
        You will receive a confirmation email shortly with your order details.
      </Message>
      
      <OrderNumber>
        <p>Your Order Number</p>
        <strong>{order?.id || 'MCF-' + Math.floor(100000 + Math.random() * 900000)}</strong>
      </OrderNumber>
      
      {order && order.orderDetails && (
        <OrderDetails>
          <h3>Order Details</h3>
          {order.orderDetails.items.map((item, index) => (
            <OrderItem key={index}>
              <ItemName>{item.name}</ItemName>
              <ItemQuantity>x{item.quantity}</ItemQuantity>
              <ItemPrice>{formatCurrency(item.price * item.quantity)}</ItemPrice>
            </OrderItem>
          ))}
          
          <OrderSummary>
            <div>
              <span>Subtotal</span>
              <span>{formatCurrency(order.orderDetails.subtotal)}</span>
            </div>
            <div>
              <span>Shipping</span>
              <span>{formatCurrency(order.orderDetails.shipping)}</span>
            </div>
            <div>
              <span>Tax</span>
              <span>{formatCurrency(order.orderDetails.tax)}</span>
            </div>
            <div className="total">
              <span>Total</span>
              <span>{formatCurrency(order.orderDetails.total)}</span>
            </div>
          </OrderSummary>
        </OrderDetails>
      )}
      
      <Message>
        If you have any questions about your order, please contact our customer service team
        with your order number.
      </Message>
      
      <ButtonGroup>
        <Button to="/dashboard">View Your Orders</Button>
        <Button to="/" primary>Continue Shopping</Button>
      </ButtonGroup>
    </Container>
  );
};

export default OrderConfirmation;
